import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Sun } from 'lucide-react';
import NepaliDatePkg from 'nepali-date-converter';
import { npDigits } from '../utils/timeAgo';
import AdBanner from '../components/common/AdBanner';

const NepaliDate = NepaliDatePkg.default || NepaliDatePkg;

// BS महिना (0 = वैशाख)
const BS_MONTHS = ['वैशाख', 'जेठ', 'असार', 'साउन', 'भदौ', 'असोज', 'कार्तिक', 'मंसिर', 'पुष', 'माघ', 'फागुन', 'चैत'];
const WEEKDAYS = ['आइत', 'सोम', 'मङ्गल', 'बुध', 'बिही', 'शुक्र', 'शनि'];
const WEEKDAYS_FULL = ['आइतबार', 'सोमबार', 'मङ्गलबार', 'बुधबार', 'बिहीबार', 'शुक्रबार', 'शनिबार'];
const AD_MONTHS = ['जनवरी', 'फेब्रुअरी', 'मार्च', 'अप्रिल', 'मे', 'जुन', 'जुलाई', 'अगस्ट', 'सेप्टेम्बर', 'अक्टोबर', 'नोभेम्बर', 'डिसेम्बर'];

const WeatherURL =
  process.env.REACT_APP_WEATHER_API_URL ||
  'https://api.open-meteo.com/v1/forecast?latitude=27.7172&longitude=85.324&current=temperature_2m,weather_code';

// BS (y, m, 1) ko AD object ra tyo mahina ka din sankhya
function bsMonthInfo(y, m) {
  const first = new NepaliDate(y, m, 1).getAD();
  const nm = m === 11 ? new NepaliDate(y + 1, 0, 1).getAD() : new NepaliDate(y, m + 1, 1).getAD();
  const asDate = (a) => new Date(a.year, a.month, a.date);
  return {
    startDay: first.day, // 0 = आइतबार
    daysInMonth: Math.round((asDate(nm) - asDate(first)) / 86400000),
    firstAD: first,
    lastAD: nm,
  };
}

function CalendarPage() {
  const todayBS = useMemo(() => new NepaliDate(new Date()).getBS(), []);
  const [view, setView] = useState({ y: todayBS.year, m: todayBS.month });
  const [weather, setWeather] = useState(null);

  React.useEffect(() => {
    let mounted = true;
    fetch(WeatherURL)
      .then((r) => r.json())
      .then((d) => {
        if (mounted && d?.current) setWeather({ temp: Math.round(d.current.temperature_2m) });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const info = useMemo(() => bsMonthInfo(view.y, view.m), [view]);
  const todayAd = useMemo(() => new NepaliDate(new Date()).getAD(), []);

  const cells = [];
  for (let i = 0; i < info.startDay; i++) cells.push(null);
  for (let d = 1; d <= info.daysInMonth; d++) cells.push(d);

  const changeMonth = (dir) => {
    setView((v) => {
      let m = v.m + dir;
      let y = v.y;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { y, m };
    });
  };

  const isThisMonth = view.y === todayBS.year && view.m === todayBS.month;
  const adRange = `${AD_MONTHS[info.firstAD.month]} ${npDigits(info.firstAD.date)} – ${AD_MONTHS[info.lastAD.month]} ${npDigits(info.lastAD.date - 1)}, ${npDigits(info.lastAD.year)}`;

  // हप्ता ko suru AD miti dekhi anta samma (sidebar ma)
  const weekStart = new Date(todayAd.year, todayAd.month, todayAd.date - todayAd.day);

  return (
    <main className="max-w-[1240px] mx-auto px-5 pt-7 pb-14 w-full">
      {/* <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-crimson mb-2.5">
        <span className="w-2 h-2 rounded-full bg-crimson" /> पात्रो
      </div> */}
      <h1 className="font-display text-[clamp(24px,3vw,32px)] font-bold mb-2">विक्रम सम्वत् पात्रो</h1>
      <p className="text-muted text-sm mb-6 max-w-[560px] leading-relaxed">
        आज {WEEKDAYS_FULL[todayBS.day]}, {BS_MONTHS[todayBS.month]} {npDigits(todayBS.date)}, {npDigits(todayBS.year)} गते चलिरहेको छ।
      </p>

      {/* पात्रो पेज माथि — ad */}
      <AdBanner position="patro_top" maxAds={1} showAll={false} className="mb-8" />

      <div className="grid gap-8 grid-cols-1 lg:grid-cols-[1.6fr_1fr] items-start">
        {/* BS महिना ग्रिड — REAL data (nepali-date-converter bata) */}
        <div className="bg-white border border-line rounded-[14px] shadow-sm overflow-hidden">
          <div className="flex items-center justify-between bg-crimson text-white px-5 py-4">
            <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20" onClick={() => changeMonth(-1)} aria-label="अघिल्लो महिना">
              <ChevronLeft size={20} />
            </button>
            <div className="text-center">
              <p className="font-display text-lg font-bold leading-tight">{BS_MONTHS[view.m]} {npDigits(view.y)}</p>
              <p className="text-[11.5px] opacity-80">{adRange}</p>
            </div>
            <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20" onClick={() => changeMonth(1)} aria-label="अर्को महिना">
              <ChevronRight size={20} />
            </button>
          </div>
          {!isThisMonth && (
            <button
              className="w-full py-2 text-[12.5px] font-bold text-crimson hover:bg-paper-soft transition-colors border-b border-line"
              onClick={() => setView({ y: todayBS.year, m: todayBS.month })}
            >
              ← आजको महिना (चालु महिना) फर्कनुहोस्
            </button>
          )}
          <div className="grid grid-cols-7 border-b border-line bg-paper-soft">
            {WEEKDAYS.map((w, i) => (
              <div key={w} className={`py-2.5 text-center text-[12px] font-bold ${i === 0 ? 'text-crimson' : 'text-ink-soft'}`}>{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const isToday = isThisMonth && d === todayBS.date;
              const isSunday = i % 7 === 0;
              // yo BS gate kun AD gate ho — grid suru dekhi gantei milauda
              const adDate = d ? new Date(info.firstAD.year, info.firstAD.month, info.firstAD.date + d - 1) : null;
              return (
                <div
                  key={i}
                  className={`min-h-[64px] p-2 border-b border-r border-line last:border-r-0 ${
                    d === null ? 'bg-paper-soft/40' : isToday ? 'bg-crimson/5' : 'hover:bg-paper-soft'
                  }`}
                >
                  {d !== null && (
                    <>
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                          isToday ? 'bg-crimson text-white font-bold shadow' : isSunday ? 'text-crimson font-semibold' : 'text-ink font-semibold'
                        }`}
                      >
                        {npDigits(d)}
                      </span>
                      {isToday && <p className="text-[10px] font-bold text-crimson mt-0.5">आज</p>}
                      {adDate && !isToday && (
                        <p className="text-[9.5px] text-muted mt-0.5">{npDigits(adDate.getDate())}</p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="flex flex-col gap-5 min-w-0">
          <div className="bg-white border border-line rounded-[14px] p-5 shadow-sm">
            <h4 className="flex items-center gap-2 text-[15px] font-bold mb-3">
              <CalendarDays size={16} className="text-crimson" /> आजको मिति
            </h4>
            <p className="font-display text-[17px] mb-1">
              {WEEKDAYS_FULL[todayBS.day]}, {BS_MONTHS[todayBS.month]} {npDigits(todayBS.date)}, {npDigits(todayBS.year)}
            </p>
            <p className="text-[13px] text-muted mb-4">
              {AD_MONTHS[todayAd.month]} {npDigits(todayAd.date)}, {npDigits(todayAd.year)} ({WEEKDAYS_FULL[todayAd.day]})
            </p>
            <div className="flex flex-col gap-2 text-[13px]">
              {[
                ['वार', WEEKDAYS_FULL[todayBS.day]],
                ['गते', `${npDigits(todayBS.date)} (${BS_MONTHS[todayBS.month]} महिना)`],
                ['यो हप्ता सुरु', `${AD_MONTHS[weekStart.getMonth()]} ${npDigits(weekStart.getDate())}`],
                ['साल', `वि.सं. ${npDigits(todayBS.year)}`],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-line pb-1.5 last:border-none">
                  <span className="text-muted">{k}</span>
                  <span className="font-semibold text-ink">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-[14px] p-5">
            <h4 className="flex items-center gap-2 text-[15px] font-bold mb-2">
              <Sun size={16} className="text-gold" /> आजको मौसम
            </h4>
            <p className="text-[13.5px] text-ink-soft leading-relaxed">
              काठमाडौंमा आज{weather ? ` ${npDigits(weather.temp)}°C` : ''} छ। विवरण हेडरमा अपडेट हुँदै आउँछ।
            </p>
          </div>
        </aside>
      </div>

      {/* पात्रो पेज तल — ad */}
      <AdBanner position="patro_bottom" maxAds={1} showAll={false} className="mt-10" />
    </main>
  );
}

export default CalendarPage;
